import { createContext, useContext, useMemo, useCallback } from 'react';

import { getItemFromLocalStorage, useLocalStorage } from './useLocalStorage';
import { FindUsersPermissionsUserByIdV5Query, School, UsersPermissionsUser } from '@edwin-group/cj-graphql';
import { getUserContext, trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { Authentication, AuthenticationContextType, AuthenticationProviderProps } from './types';
import { usePickUpWhereYouLeftOff } from '@/pages/home/hooks/usePickUpWhereYouLeftOff';

export const AuthenticationContext = createContext({} as AuthenticationContextType);

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [authentication, setAuthentication] = useLocalStorage<Authentication>('authentication', {
    isAuthenticated: false,
    accessToken: '',
    user: {} as Omit<UsersPermissionsUser, 'school'>,
  });

  const [schoolContext, setSchoolContext] = useLocalStorage<School | null>('schoolContext', null);
  const { setLastChapterOpened } = usePickUpWhereYouLeftOff();

  const updateUserContext = useCallback(
    (data: FindUsersPermissionsUserByIdV5Query | null) => {
      const userData = JSON.parse(JSON.stringify(data?.usersPermissionsUser)) as UsersPermissionsUser | null;
      const mySchools = data?.fetchMySchools;
      const schoolContextSelected = mySchools?.find(
        (school) => school.documentId === (schoolContext?.documentId || ''),
      );

      // if the school selected not exists, set the school context to the school from CMS
      const school = !schoolContextSelected ? userData?.school : schoolContextSelected;
      if (userData) {
        userData.school = school as School;
      }

      setSchoolContext(school as School);
      setAuthentication({
        accessToken: authentication?.accessToken || '',
        isAuthenticated: authentication?.isAuthenticated || false,
        user: userData as UsersPermissionsUser,
      });

      trackGAEvent('user_context', getUserContext(userData as UsersPermissionsUser));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolContext?.documentId],
  );

  const updateSchoolContext = useCallback(
    (school: School) => {
      const newUserData = { ...authentication?.user, school };
      setAuthentication({
        accessToken: authentication?.accessToken || '',
        isAuthenticated: authentication?.isAuthenticated || false,
        user: newUserData as UsersPermissionsUser,
      });
      setSchoolContext(school);
      trackGAEvent('user_context', getUserContext(newUserData as UsersPermissionsUser));
    },
    [authentication, setAuthentication, setSchoolContext],
  );

  const loginAuth = useCallback(
    async (authenticationCallback: Authentication) => {
      setAuthentication({
        isAuthenticated: true,
        accessToken: authenticationCallback.accessToken,
        user: authenticationCallback.user,
      });
      setSchoolContext((authenticationCallback.user as UsersPermissionsUser).school || null);
    },
    [setAuthentication, setSchoolContext],
  );

  const toggleOnboardingStatus = useCallback(
    (onboarding: 'onboardCharacterCompassCompleted' | 'onboardHomeCompleted') => {
      setAuthentication({
        accessToken: authentication?.accessToken || '',
        isAuthenticated: authentication?.isAuthenticated || false,
        user: { ...authentication?.user, [onboarding]: !authentication?.user?.[onboarding] } as UsersPermissionsUser,
      });
    },
    [authentication, setAuthentication],
  );

  const logout = useCallback(() => {
    setAuthentication({
      isAuthenticated: false,
      accessToken: '',
      user: {} as UsersPermissionsUser,
    });
    setLastChapterOpened(null);
    setSchoolContext(null);
    window.location.href = '/login';
  }, [setAuthentication, setSchoolContext, setLastChapterOpened]);

  const value = useMemo(
    () => ({
      authentication,
      schoolContext,
      toggleOnboardingStatus,
      updateUserContext,
      updateSchoolContext,
      loginAuth,
      logout,
    }),
    [authentication, schoolContext, updateUserContext, updateSchoolContext, loginAuth, logout, toggleOnboardingStatus],
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

export const getAuthentication = () => {
  return (getItemFromLocalStorage<Authentication>('authentication') || {}) as Authentication;
};

export const getSchoolContext = () => {
  return (getItemFromLocalStorage<School>('schoolContext') || null) as School | null;
};

export const useAuthentication = () => {
  return useContext(AuthenticationContext);
};
